import { useMemo, useState } from "react";
import PageHeader from "@/components/feature/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import CastCard from "@/components/cards/CastCard";
import { castList } from "@/mocks/cast";
import { useSeo } from "@/hooks/useSeo";

const initials: Record<string, string> = {
  林晚晴: "L",
  陈知遥: "C",
  苏念: "S",
  周叙: "Z",
  顾青: "G",
  白露: "B",
  何竞: "H",
  秦朗: "Q",
};

export default function CastPage() {
  const [roleType, setRoleType] = useState("全部");
  const [letter, setLetter] = useState("全部");

  useSeo({
    title: "演员阵容 - 蜜桃成熟时3D",
    description:
      "蜜桃成熟时3D演员阵容与角色资料，包含主演与配角列表，支持按角色类型和姓名首字母快速筛选浏览。",
    keywords: "蜜桃成熟时3D演员,演员阵容,角色资料,主演名单",
    path: "/cast",
  });

  const letters = useMemo(() => {
    const set = new Set(castList.map((item) => initials[item.name] ?? "#"));
    return ["全部", ...Array.from(set).sort()];
  }, []);

  const filtered = useMemo(() => {
    return castList.filter((item) => {
      const roleOk = roleType === "全部" || item.roleType === roleType;
      const letterOk = letter === "全部" || (initials[item.name] ?? "#") === letter;
      return roleOk && letterOk;
    });
  }, [roleType, letter]);

  const letterBtn = (active: boolean) =>
    `flex h-8 w-8 items-center justify-center rounded-md text-sm transition-colors ${
      active
        ? "bg-accent-500 text-background-50"
        : "border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-400 hover:text-primary-700"
    }`;

  return (
    <>
      <PageHeader
        eyebrow="CAST"
        title="演员阵容"
        description="了解「蜜桃成熟时3D」的演员阵容与角色资料。你可以按主演 / 配角筛选，也可以按姓名首字母快速定位想要了解的演员。"
        crumbs={[{ label: "演员阵容" }]}
      >
        <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 text-sm text-foreground-500">角色类型</span>
            {["全部", "主演", "配角"].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setRoleType(option)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                  roleType === option
                    ? "bg-secondary-500 text-background-50"
                    : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-sm text-foreground-500">姓名首字母</span>
            {letters.map((item) => (
              <button key={item} type="button" onClick={() => setLetter(item)} className={letterBtn(letter === item)}>
                {item === "全部" ? "全部" : item}
              </button>
            ))}
          </div>
        </div>
      </PageHeader>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6">
          <p className="mb-6 text-sm text-foreground-500">
            当前显示 <span className="font-medium text-primary-700">{filtered.length}</span> 位演员
          </p>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
              {filtered.map((item) => (
                <CastCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-user-search-line"
              title="没有符合条件的演员"
              description="试着调整角色类型或姓名首字母筛选条件。"
              actionLabel="重置筛选"
              onAction={() => { setRoleType("全部"); setLetter("全部"); }}
            />
          )}
        </div>
      </section>
    </>
  );
}